import axios from 'axios';
import Pristine from 'pristinejs';
import { debounce } from 'lodash';
import {
  serialize,
  getWindowHeight,
  openModalWhenReady,
  logErrorToSentry,
} from '../helpers/helpers';
import { pushDataLayerEvent } from '../helpers/data-layer';
import { createFormAnalytics, watchModalOpen } from '../helpers/form-analytics';
import { repPhotoMarkup, swapFailedRepPhotos } from '../helpers/rep-photo';
import formLabelAnimation from './form-label-animation';

const modalName = 'modal-find-rep';

const formFindRep = (function() {
  const modalContainer = document.querySelector(
      `#${modalName} .modal__container`
    ),
    panelForm = document.querySelector(`#${modalName} .panel__group--form`),
    panelResults = document.querySelector('.panel__group--results'),
    panelNoResults = document.querySelector('.panel__group--no-results'),
    findForm = document.getElementById('find-rep'),
    inputSelectorClass = '.custom-input',
    hiddenClass = 'is-hidden',
    repApi = '//api.3-form.com/rep_search/?',
    formAnalytics = createFormAnalytics(
      'Contact|Find a Consultant',
      () => findForm
    );

  let formInputs,
    submitButton,
    closeButton,
    findAnotherRepButton,
    countryDropdown,
    zipcodeField,
    emailInput,
    modalNonExpandedHeight,
    phoneInput;

  /**
   *
   * @param repData - rep data object
   * @param isRep - boolean
   */
  function repCardTemplate(repData, isRep) {
    const repType = isRep ? repData.rep : repData;
    return `
      <div class="content-card w-full">
        <div class="card-driver--border">
          <div class="content-card__content">
            <div class="flex mt-6">
              <div class="flex w-1/4 mx-6">
                <div class="rep-form__image--outer">
                  ${repPhotoMarkup(repType.image)}
                </div>
              </div>
              <div class="w-3/4">
                <ul class="list-reset">
                  <li class="mb-4">
                    <p class="p font-medium"><span>${
                      repType.fname
                    }</span> <span>${repType.lname}</span></p>
                    <p class="p"> ${
                      isRep
                        ? `Materials Consultant, ${repType.territory}`
                        : 'Customer Support'
                    }</p>
                  </li>
                  <li class="mb-2">
                    <svg class="icon icon-telephone mr-2"><use xlink:href="#icon-telephone"></use></svg><a href="tel:${
                      repType.phone
                    }" class="a">${repType.phone}</a>
                  </li>
                  <li class="mb-2">
                    <svg class="icon icon-email mr-2"><use xlink:href="#icon-email"></use></svg><a href="mailto:${
                      repType.email
                    }" class="a">${repType.email}</a>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>
    `;
  }
  /**
   *
   * @param repData - rep data object
   */
  function renderRepCards(repData) {
    const salesContainer = document.querySelector(
        `#${modalName}-content .rep-results--sales`
      ),
      pricingContainer = document.querySelector(
        `#${modalName}-content .rep-results--pricing`
      ),
      tooManyRepsContainer = document.querySelector(
        `#${modalName}-content .rep-results--too-many-reps`
      ),
      halfPanelClass = 'panel__row--inner-half',
      fullPanelClass = 'panel__row--inner-full',
      fewReps = repData.length < 3;
    if (repData.length === 1) {
      // if only one rep
      tooManyRepsContainer.parentNode.classList.add('hidden');
      salesContainer.innerHTML += repCardTemplate(repData[0], true);
      // if sales rep has pricing rep
      if (repData[0].rep.customer_support) {
        pricingContainer.innerHTML += repCardTemplate(
          repData[0].rep.customer_support,
          false
        );
      }
      // size rep panel widths
      salesContainer.parentNode.classList.add(halfPanelClass);
      pricingContainer.parentNode.classList.add(halfPanelClass);
    } else if (repData.length > 1) {
      // if more than one rep and less than three
      if (fewReps) {
        for (let i = 0; i < 2; i++) {
          salesContainer.innerHTML += repCardTemplate(repData[i], true);
        }
        tooManyRepsContainer.parentNode.classList.add('hidden');
      } else {
        salesContainer.parentNode.classList.add('hidden');
      }
      // if sales rep has pricing rep
      if (repData[0].rep.customer_support) {
        pricingContainer.innerHTML += repCardTemplate(
          repData[0].rep.customer_support,
          false
        );
      }
      // size rep panel widths
      salesContainer.parentNode.classList.add(fullPanelClass);
      pricingContainer.parentNode.classList.add(halfPanelClass);
    } else if (repData.length < 1) {
      // if no reps found
      panelForm.classList.add(hiddenClass);
      panelNoResults.classList.remove(hiddenClass);
    }
    // show/hide form + results blocks
    if (
      !panelForm.classList.contains(hiddenClass) &&
      panelResults.classList.contains(hiddenClass) &&
      repData.length
    ) {
      panelResults.classList.remove(hiddenClass);
      panelForm.classList.add(hiddenClass);
      if (fewReps) {
        modalNonExpandedHeight = modalContainer.style.height;
        let viewportHeight = getWindowHeight(),
          expandedHeight = viewportHeight - Math.floor(0.05 * viewportHeight);
        modalContainer.style.height = `${expandedHeight}px`;
      }
      modalContainer.scrollTo({
        top: 0,
        behavior: 'smooth',
      });
    }
  }
  /**
   *
   * @param url - rep info API
   * @param formData - form data
   */
  function getApiData(url, formData) {
    axios
      .get(url + formData)
      .then(response => {
        const repData = response.data;

        if (repData[0]?.rep.fname === 'Open') {
          // Contact information needs to go to interim reps if position is open
          repData[0].rep.phone = repData[0].rep.customer_support.phone;
          repData[0].rep.email = 'designsupport@3-form.com';
          repData[0].rep.image = '';
        }

        renderRepCards(repData);
        sendAnalyticsEvent('submit');
        formAnalytics.submit();
      })
      .catch(error => {
        formAnalytics.error();
        logErrorToSentry(error);
      });
  }
  /**
   *
   * @param modal - modal
   */
  function submitButtonValidation(modal) {
    let canSubmit = true;
    submitButton = modal.querySelector('.js-form-submit');
    formInputs = modal.querySelectorAll(
      `${inputSelectorClass} input[type="text"],
      ${inputSelectorClass} select`
    );
    emailInput = modal.querySelector(
      `${inputSelectorClass} input[type="email"]`
    );
    phoneInput = modal.querySelector(
      `${inputSelectorClass} input[type="phone"]`
    );
    if (emailInput || phoneInput) {
      if (emailInput.value === '' && phoneInput.value === '') canSubmit = false;
    }
    [...formInputs].forEach(input => {
      if (input.value === '') canSubmit = false;
    });
    submitButton.disabled = !canSubmit;
  }
  function resetResults() {
    const rowToClear = document.querySelectorAll(
      '.panel__row--inner-reps .rep-results'
    );
    // find rep form
    rowToClear.forEach(row => {
      row.parentNode.className = 'panel__row--inner panel__row--inner-reps';
      if (row.innerHTML) {
        // delay added to match CSS transitions
        setTimeout(() => {
          row.innerHTML = '';
        }, 100);
      }
    });
  }
  /**
   *
   * @param e - event
   */
  function pullRepData(e) {
    e.preventDefault();
    e.stopPropagation();
    resetResults();
    if (submitButton.disabled === false) {
      const form = e.target.parentNode,
        formData = serialize(form);
      getApiData(repApi, formData);
    }
  }
  /**
   *
   * @param modal - modal
   */
  function resetForm(modal) {
    countryDropdown = modal.querySelector('.js-form-country');
    zipcodeField = modal.querySelector('.js-form-zip');
    // show/hide form + results blocks
    if (panelForm.classList.contains(hiddenClass)) {
      panelResults.classList.add(hiddenClass);
      panelNoResults.classList.add(hiddenClass);
      panelForm.classList.remove(hiddenClass);
    }
    // reset inputs
    countryDropdown.selectedIndex = 0;
    zipcodeField.value = '';
    formLabelAnimation.toggleValidClass(countryDropdown);
    formLabelAnimation.toggleValidClass(zipcodeField);
    // reset the form + results
    resetResults();
    submitButtonValidation(modal);
    // a reset form starts a fresh form:start / form:submit pair
    formAnalytics.end();
    modalContainer.style.height = modalNonExpandedHeight;
    modalContainer.scrollTo({
      top: 0,
      behavior: 'smooth',
    });
  }

  function sendAnalyticsEvent(action) {
    // A rep lookup isn't a lead, so it stays out of GA4's generate_lead.
    if (action === 'submit') {
      pushDataLayerEvent('rep_search', { lead_type: 'find_a_rep' });
      return;
    }

    pushDataLayerEvent('rep_search_repeat', { lead_type: 'find_a_rep' });
  }

  function init() {
    const modal = document.getElementById(modalName),
      pristine = new Pristine(findForm);

    if (modal) {
      swapFailedRepPhotos();
      submitButton = modal.querySelector('.js-form-submit');
      closeButton = modal.querySelector('.js-close-panel');
      findAnotherRepButton = modal.querySelector('.js-find-another-rep');
      countryDropdown = modal.querySelector('.js-form-country');
      formInputs = modal.querySelectorAll(
        `${inputSelectorClass} input[type="text"],
        ${inputSelectorClass} input[type="email"],
        ${inputSelectorClass} input[type="phone"],
        ${inputSelectorClass} select`
      );
      // bind validation listeners once — covers typing, paste, autofill,
      // and select changes regardless of how the modal was opened
      const runValidation = debounce(() => submitButtonValidation(modal), 300);
      [...formInputs].forEach(input => {
        input.addEventListener('input', runValidation);
        input.addEventListener('change', runValidation);
      });
      findForm.addEventListener('input', () => formAnalytics.start());
      watchModalOpen(modal, {
        onOpen: () => formAnalytics.view(),
        onClose: () => formAnalytics.end(),
      });
      // reset form when re-opened via a `.js-find-rep` trigger
      document.addEventListener('click', function(e) {
        if (!e.target.classList.contains('js-find-rep')) return;
        resetForm(modal);
      });
      // get rep data on submit
      submitButton.addEventListener('click', function(e) {
        const isValid = pristine.validate();
        e.preventDefault();
        if (isValid) pullRepData(e);
        else formAnalytics.error();
      });
      // form resets
      closeButton.addEventListener('click', function() {
        resetForm(modal);
      });
      findAnotherRepButton.addEventListener('click', function() {
        resetForm(modal);
        sendAnalyticsEvent('find another');
      });
      document.addEventListener('keydown', function(e) {
        if (e.key === 'Escape' && modal.classList.contains('is-open')) {
          resetForm(modal);
        }
      });

      const urlFragment = window.location.hash,
        openModal =
          urlFragment === '#find-rep' || urlFragment === '#getstarted';

      if (openModal) openModalWhenReady(modal);
    }
  }
  return {
    init,
    submitButtonValidation,
  };
})();
export default formFindRep;
